import type {
  OTEntry,
  OTFormulaCard,
  OTFormulaCardTypeMapping,
  OvertimeEntrySnapshotInput,
} from 'api/gql-utils/overtime/types';
import {
  OVERTIME_TYPE_FROM_API_VALUE,
  OVERTIME_TYPE_LABELS,
} from 'components/Settings/AttendanceAndLeavesSettings/constants';
import { LocalEntry, OtFormulaCardWithLabel } from './types';

export function apiDataToLocalOTState(entry: OTEntry): LocalEntry {
  return {
    id: entry.id,
    isNew: false,
    otFormulaCardId: entry.formulaCardId,
    totalOtHours: entry.totalOtHours,
    totalOtDays: entry.totalOtDays,
    workingHoursPerDay: entry.workingHoursPerDay,
    source: entry.source,
    calculatedAmount: entry.computedAmount ? (entry.computedAmount || 0) / 100 : null,
    isManuallyEdited: entry.overrideAmount != null,
    overrideAmount: entry.overrideAmount ? entry.overrideAmount / 100 : null,
  };
}

export function localOtStateToApiPayload(
  entry: LocalEntry,
  baseDays: string,
): OvertimeEntrySnapshotInput {
  return {
    formulaCardId: entry.otFormulaCardId!,
    totalOtHours: entry.totalOtHours || undefined,
    totalOtDays: entry.totalOtDays || undefined,
    workingHoursPerDay: entry.workingHoursPerDay,
    baseDays,
    overrideAmount: entry.isManuallyEdited
      ? entry.overrideAmount
        ? entry.overrideAmount * 100
        : undefined
      : undefined,
  };
}

export function formatFormulaDescription(card: OTFormulaCard): string {
  const parts: string[] = [];
  if (card.rateType === 'multiplier' || card.rateType === 'flat_plus_multiplier') {
    const unit = card.rateUnit === 'per_hour' ? 'hourly' : 'daily';
    parts.push(`${card.multiplier}x ${unit} rate`);
  }
  if (card.rateType === 'flat' || card.rateType === 'flat_plus_multiplier') {
    const flatRs = card.flatRate != null ? (card.flatRate / 100).toFixed(0) : '0';
    const unit = card.rateUnit === 'per_hour' ? '/hr' : '/day';
    parts.push(`₹${flatRs}${unit} flat`);
  }
  if (card.dailyAllowance != null && card.dailyAllowance > 0) {
    parts.push(`+ ₹${(card.dailyAllowance / 100).toFixed(0)}/day`);
  }
  return parts.join(' + ') || card.formulaTemplate;
}

export const computeRowDisplayedAmount = (row: LocalEntry): number => {
  if (row.isManuallyEdited) return row.overrideAmount ?? 0;
  return row.calculatedAmount ?? 0;
};

export function applyUbitechFormulaCards(
  formulaCards: OTFormulaCard[],
  typeMappings: OTFormulaCardTypeMapping[],
): Array<OtFormulaCardWithLabel> {
  const mappedCardIds = new Set(typeMappings.map((m) => m.formulaCardId));
  const labelByCardId = new Map<string, string>();
  typeMappings.forEach((mapping) => {
    const type = OVERTIME_TYPE_FROM_API_VALUE[mapping.overtimeType];
    if (!type) return;
    labelByCardId.set(mapping.formulaCardId, OVERTIME_TYPE_LABELS[type]);
  });

  return formulaCards
    .filter((card) => mappedCardIds.has(card.id))
    .map((card) => {
      const label = labelByCardId.get(card.id);
      return label ? { ...card, label } : card;
    });
}
